import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useUnitPreference } from '../../contexts/UnitPreferenceContext'
import { formatPressure as formatPressureUtil } from '../../utils/unitConversion'
import { OCAColors, Typography, Spacing } from '../../theme/colors'
import { IOSListSection } from '../ui/IOSListSection'
import { IOSListItem } from '../ui/IOSListItem'
import { MaterialIcon } from '../ui/MaterialIcon'
import { StatCard } from '../ui/StatCard'
import { useDataStore } from '../../store/dataStore'
import type { Device as APIDevice, Location as APILocation, DeviceData } from '../../services/api'
import { getDeviceStatus as getDeviceStatusUtil } from '../../utils/deviceStatus'

// Enhanced types based on real API data
interface Zone {
  id: string
  name: string
  type: string
  currentPressure: number
  targetPressure: number
  status: 'normal' | 'warning' | 'alarm' | 'offline'
  lastUpdate: Date
  devices: EnhancedDevice[]
  location?: APILocation
}

interface EnhancedDevice extends APIDevice {
  currentData?: DeviceData
  status: 'online' | 'offline' | 'warning' | 'alarm'
  pressure: number
  batteryLevel?: number
  signalStrength?: number
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, getAccessToken } = useAuth()
  const { pressureUnit } = useUnitPreference()

  // Global state from Zustand store
  const {
    devices: storeDevices,
    locations: storeLocations,
    deviceData: storeDeviceData,
    isLoadingDevices,
    isLoadingLocations,
    isLoadingDeviceData,
    devicesError,
    locationsError,
    deviceDataError,
    fetchAllData
  } = useDataStore()

  const [zones, setZones] = useState<Zone[]>([])
  // const [alarms, setAlarms] = useState<Alarm[]>([])

  // Function to determine device status using shared utility
  const getDeviceStatus = (device: EnhancedDevice): 'online' | 'offline' | 'warning' | 'alarm' => {
    if (!device.currentData) return 'offline'

    // Use shared device status utility with correct priority
    const status = getDeviceStatusUtil(
      device.currentData.dp0_alarm_state,
      device.currentData.dp1_alarm_state,
      device.currentData.timestamp
    )

    // Map DeviceStatus type to our local status type
    switch (status) {
      case 'Alarm (Offline)':
      case 'Alarm':
        return 'alarm'
      case 'Warning':
        return 'warning'
      case 'Offline':
      case 'No Data':
        return 'offline'
      case 'Online':
      default:
        return 'online'
    }
  }

  // Function to determine zone status based on devices
  const getZoneStatus = (devices: EnhancedDevice[]): 'normal' | 'warning' | 'alarm' | 'offline' => {
    if (devices.length === 0) return 'offline'

    const statuses = devices.map(device => device.status)

    if (statuses.includes('alarm')) return 'alarm'
    if (statuses.includes('warning')) return 'warning'
    if (statuses.every(status => status === 'offline')) return 'offline'

    return 'normal'
  }

  // Build zones from locations and devices
  const buildZonesFromData = (locations: APILocation[], devices: APIDevice[], deviceDataMap: Map<string, DeviceData>) => {
    // Group devices by location
    const devicesByLocation = new Map<string, EnhancedDevice[]>()

    devices.forEach(device => {
      if (!device.locationId) return

      const currentData = deviceDataMap.get(device.deviceId)
      const enhancedDevice: EnhancedDevice = {
        ...device,
        currentData,
        status: getDeviceStatus({
          ...device,
          currentData,
          pressure: currentData?.dp0 || 0
        } as EnhancedDevice),
        pressure: currentData?.dp0 || 0,
        batteryLevel: undefined, // Not available in current API
        signalStrength: currentData?.c_rssi // Use connection RSSI
      }

      enhancedDevice.status = getDeviceStatus(enhancedDevice)

      if (!devicesByLocation.has(device.locationId)) {
        devicesByLocation.set(device.locationId, [])
      }
      devicesByLocation.get(device.locationId)!.push(enhancedDevice)
    })

    // Create zones from locations that have devices
    const zonesData: Zone[] = []
    locations.forEach((location: APILocation) => {
      const locationDevices = devicesByLocation.get(location.locationId) || []
      if (locationDevices.length === 0) return // Skip locations without devices

      const avgPressure = locationDevices.length > 0
        ? locationDevices.reduce((sum, device) => sum + device.pressure, 0) / locationDevices.length
        : 0

      const zone: Zone = {
        id: location.locationId,
        name: location.name || location.locationId,
        type: location.address || 'Monitoring Zone',
        currentPressure: avgPressure,
        targetPressure: 0.0, // Default target, could be configured
        status: getZoneStatus(locationDevices),
        lastUpdate: new Date(),
        devices: locationDevices,
        location
      }

      zonesData.push(zone)
    })

    return zonesData
  }

  // Refresh data from API (force refresh)
  const refreshData = async () => {
    if (!isAuthenticated) return
    const token = await getAccessToken()
    if (!token) return
    await fetchAllData(token, true) // Force refresh
  }

  // Fetch data from store on mount
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) return

      const token = await getAccessToken()
      if (!token) {
        console.error('Unable to get access token')
        return
      }

      console.log('📊 Dashboard: Fetching data from store...')
      await fetchAllData(token) // Uses cache
    }

    loadData()
  }, [isAuthenticated, getAccessToken, fetchAllData])

  // Process store data into zones whenever it changes
  useEffect(() => {
    if (storeDevices.length === 0 || storeLocations.length === 0) return

    console.log('📊 Dashboard: Building zones from store data...')
    console.log('📊 Dashboard: Devices:', storeDevices.length, 'Locations:', storeLocations.length)

    // Convert Record to Map for buildZonesFromData
    const deviceDataMap = new Map<string, DeviceData>()
    Object.entries(storeDeviceData).forEach(([deviceId, dataArray]) => {
      if (dataArray.length > 0) {
        deviceDataMap.set(deviceId, dataArray[0]) // Most recent data
      }
    })

    console.log('📊 Dashboard: Device data map size:', deviceDataMap.size)

    // Build zones from store data
    const zonesData = buildZonesFromData(storeLocations, storeDevices, deviceDataMap)
    console.log('📊 Dashboard: Built', zonesData.length, 'zones')

    setZones(zonesData)
  }, [storeDevices, storeLocations, storeDeviceData])

  // Get status statistics - count devices, not zones
  const allDevices = zones.flatMap(zone => zone.devices)
  const normalDevices = allDevices.filter(device => device.status === 'online')
  const warningDevices = allDevices.filter(device => device.status === 'warning')
  const alarmDevices = allDevices.filter(device => device.status === 'alarm')
  const offlineDevices = allDevices.filter(device => device.status === 'offline')

  // Keep zone filters for displaying alarm/warning sections
  const warningZones = zones.filter(zone => zone.status === 'warning')
  const alarmZones = zones.filter(zone => zone.status === 'alarm')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'alarm': return OCAColors.danger
      case 'warning': return OCAColors.warning
      case 'normal': return OCAColors.success
      case 'offline': return OCAColors.gray500
      default: return OCAColors.gray500
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'alarm': return 'error'
      case 'warning': return 'warning'
      case 'normal': return 'check_circle'
      case 'offline': return 'signal_wifi_off'
      default: return 'help'
    }
  }

  // Get subtitle for zone - only show pressure if there's exactly one device
  const getZoneSubtitle = (zone: Zone) => {
    if (zone.devices.length === 1) {
      return `${zone.type} • ${formatPressureUtil(zone.currentPressure, pressureUnit)}`
    }
    return zone.type
  }

  const handleZoneTap = (zone: Zone) => {
    console.log('Zone tapped:', zone.name, 'navigating to devices with filter')
    // Navigate to devices tab with location filter
    navigate('/devices', {
      state: {
        locationFilter: zone.id,
        locationName: zone.name
      }
    })
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        textAlign: 'center',
        padding: Spacing.xl,
        color: OCAColors.textSecondary,
      }}>
        Please log in to view dashboard
      </div>
    )
  }

  // Show loading state while any data is loading
  const loading = isLoadingDevices || isLoadingLocations || isLoadingDeviceData
  const error = devicesError || locationsError || deviceDataError

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
        paddingBottom: '140px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Spacing.xl,
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${OCAColors.separator}`,
          borderTop: `3px solid ${OCAColors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: Spacing.md,
        }} />
        <p style={{
          color: OCAColors.secondaryLabel,
          margin: 0,
        }}>
          Loading dashboard data...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
        paddingBottom: '140px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Spacing.xl,
      }}>
        <MaterialIcon name="error" size={48} color={OCAColors.danger} />
        <h2 style={{
          fontSize: Typography.fontSize.lg,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          margin: `${Spacing.md} 0 ${Spacing.sm} 0`,
        }}>
          Error Loading Dashboard
        </h2>
        <p style={{
          color: OCAColors.secondaryLabel,
          margin: `0 0 ${Spacing.lg} 0`,
          textAlign: 'center',
        }}>
          {error}
        </p>
        <button
          onClick={refreshData}
          style={{
            backgroundColor: OCAColors.primary,
            color: OCAColors.background,
            border: 'none',
            borderRadius: '8px',
            padding: `${Spacing.sm} ${Spacing.lg}`,
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.medium,
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: '#F2F2F7',
      minHeight: '100vh',
      paddingBottom: '140px',
    }}>
      {/* Header */}
      <div style={{
        padding: Spacing.lg,
        backgroundColor: OCAColors.gray900,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        minHeight: '96px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: Spacing.sm,
          marginBottom: Spacing.sm,
        }}>
          <img
            src="/OmniIQ_AppIcon_1024x1024.png"
            alt="OMNI IQ"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
            }}
          />
          <h1 style={{
            fontSize: Typography.fontSize['2xl'],
            fontWeight: Typography.fontWeight.bold,
            color: OCAColors.background,
            margin: 0,
          }}>
            Dashboard
          </h1>
        </div>
        <p style={{
          fontSize: Typography.fontSize.sm,
          color: 'rgba(255, 255, 255, 0.7)',
          margin: 0,
        }}>
          Welcome back, {user?.name || 'User'}
        </p>
      </div>

      {/* Status Overview */}
      <div style={{ padding: `${Spacing.lg} ${Spacing.lg} 0 ${Spacing.lg}` }}>
        <h2 style={{
          fontSize: Typography.fontSize.xs,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.secondaryLabel,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          marginBottom: Spacing.md,
          marginLeft: Spacing.xs,
        }}>
          SYSTEM STATUS
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: Spacing.md,
          marginBottom: Spacing.lg,
        }}>
          <StatCard
            value={normalDevices.length}
            label="Normal"
            color={OCAColors.success}
            icon="✓"
          />
          <StatCard
            value={warningDevices.length}
            label="Warnings"
            color={OCAColors.warning}
            icon="⚠"
            trend={warningDevices.length > 0 ? 'up' : undefined}
          />
          <StatCard
            value={alarmDevices.length}
            label="Alarms"
            color={OCAColors.danger}
            icon="!"
            trend={alarmDevices.length > 0 ? 'up' : undefined}
          />
          <StatCard
            value={offlineDevices.length}
            label="Offline"
            color={OCAColors.gray500}
            icon="○"
          />
        </div>
      </div>

      {/* Active Alarms */}
      {alarmZones.length > 0 && (
        <IOSListSection title="ACTIVE ALARMS">
          {alarmZones.map((zone) => (
            <IOSListItem
              key={zone.id}
              icon={
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  backgroundColor: getStatusColor(zone.status),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MaterialIcon name={getStatusIcon(zone.status)} size={20} color={OCAColors.background} />
                </div>
              }
              title={zone.name}
              subtitle={getZoneSubtitle(zone)}
              value={`${zone.devices.length} device${zone.devices.length !== 1 ? 's' : ''}`}
              onClick={() => handleZoneTap(zone)}
              status="Alarm"
              statusColor={getStatusColor(zone.status)}
            />
          ))}
        </IOSListSection>
      )}

      {/* Warnings */}
      {warningZones.length > 0 && (
        <IOSListSection title="WARNINGS">
          {warningZones.map((zone) => (
            <IOSListItem
              key={zone.id}
              icon={
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  backgroundColor: getStatusColor(zone.status),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MaterialIcon name={getStatusIcon(zone.status)} size={20} color={OCAColors.background} />
                </div>
              }
              title={zone.name}
              subtitle={getZoneSubtitle(zone)}
              value={`${zone.devices.length} device${zone.devices.length !== 1 ? 's' : ''}`}
              onClick={() => handleZoneTap(zone)}
              status="Warning"
              statusColor={getStatusColor(zone.status)}
            />
          ))}
        </IOSListSection>
      )}

      {/* All Zones */}
      <IOSListSection title="ALL ZONES">
        {zones.map((zone) => (
          <IOSListItem
            key={zone.id}
            icon={
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: getStatusColor(zone.status),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MaterialIcon name={getStatusIcon(zone.status)} size={20} color={OCAColors.background} />
              </div>
            }
            title={zone.name}
            subtitle={getZoneSubtitle(zone)}
            value={`${zone.devices.length} device${zone.devices.length !== 1 ? 's' : ''}`}
            onClick={() => handleZoneTap(zone)}
          />
        ))}
      </IOSListSection>
    </div>
  )
}